import { useEffect, useMemo, useState } from 'react'
import { createClient, getUserApiKey } from '@/lib/supabase-client'

type JobType = 'check' | 'submit'

interface IndexJob {
  id: string
  type: JobType
  total_urls: number
  processed_urls: number
  indexed_count?: number | null
  submitted_count?: number | null
  status: 'pending' | 'running' | 'complete' | 'failed'
  error_message?: string | null
  created_at: string
}

interface IndexJobUrl {
  id: string
  url: string
  status: 'pending' | 'processing' | 'done' | 'failed'
  is_indexed?: boolean | null
  // Set on submit jobs: the IndexNow status for the batch this URL belonged to
  submit_status?: string | null
  host?: string | null
  // Optional raw_result from the API – we use this to detect rate-limited URLs
  raw_result?: {
    reason?: 'indexed' | 'not_indexed' | 'rate_limited' | 'error' | string
  } | null
  created_at: string
  updated_at: string
}

// A domain registered for Index Submit. IndexNow proves ownership with a key
// file at the site root, so a domain is unusable until `verified` is true.
interface IndexNowKey {
  id: string
  host: string
  key: string
  key_location?: string | null
  verified: boolean
  verified_at?: string | null
  last_error?: string | null
}

type TabKey = 'check' | 'submit'

// Index Submit uses IndexNow, which Google does not participate in.
const INDEXNOW_ENGINES = 'Bing, Yandex, Seznam and Naver'

export default function IndexingPage() {
  const supabase = createClient()
  const [activeTab, setActiveTab] = useState<TabKey>('check')
  const [urlsInput, setUrlsInput] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [jobs, setJobs] = useState<IndexJob[]>([])
  const [jobsLoading, setJobsLoading] = useState(false)
  const [selectedJob, setSelectedJob] = useState<IndexJob | null>(null)
  const [jobUrls, setJobUrls] = useState<IndexJobUrl[]>([])
  const [jobUrlsLoading, setJobUrlsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // IndexNow domain state (Index Submit only)
  const [domains, setDomains] = useState<IndexNowKey[]>([])
  const [domainsLoading, setDomainsLoading] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [registering, setRegistering] = useState(false)
  const [verifyingHost, setVerifyingHost] = useState<string | null>(null)
  // Shown right after registering so the user can copy the key file details.
  const [pendingSetup, setPendingSetup] = useState<IndexNowKey | null>(null)

  const apiBaseUrl = import.meta.env.VITE_SEO_API_BASE_URL || 'https://api.gistmag.co.uk'

  const urlStats = useMemo(() => {
    const lines = urlsInput
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
    const unique = Array.from(new Set(lines))
    return {
      total: lines.length,
      unique: unique.length,
    }
  }, [urlsInput])

  const hasVerifiedDomain = useMemo(() => domains.some((d) => d.verified), [domains])

  const getApiKey = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('Please sign in to use the Indexing tools')
    }

    const apiKey = await getUserApiKey(supabase, session.user.id)

    if (!apiKey) {
      throw new Error('Unable to retrieve API key. Please try refreshing the page.')
    }

    return apiKey
  }

  useEffect(() => {
    loadJobs()
    // Domains are only relevant to Index Submit.
    if (activeTab === 'submit') loadDomains()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  // Auto-refresh every 10s if the selected job is still running/pending
  useEffect(() => {
    if (!selectedJob || (selectedJob.status !== 'running' && selectedJob.status !== 'pending')) return

    const interval = setInterval(async () => {
      try {
        const key = await getApiKey()
        // Refresh the selected job
        const jobRes = await fetch(
          `${apiBaseUrl}/indexing/jobs/${selectedJob.id}?api_key=${encodeURIComponent(key)}`
        )
        if (jobRes.ok) {
          const freshJob = (await jobRes.json()) as IndexJob
          setSelectedJob(freshJob)
          // Also refresh the jobs list so the sidebar updates
          loadJobs()
          // Refresh URLs
          await loadJobUrls(freshJob.id, key)
          // Stop polling if job is done
          if (freshJob.status === 'complete' || freshJob.status === 'failed') {
            clearInterval(interval)
          }
        }
      } catch {
        // ignore polling errors
      }
    }, 10_000)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob?.id, selectedJob?.status])

  const loadJobs = async () => {
    try {
      setJobsLoading(true)
      setError(null)
      const apiKey = await getApiKey()
      if (!apiKey) {
        setError('No API key found. Please create one on the API Key page.')
        setJobs([])
        return
      }

      const url = new URL(`${apiBaseUrl}/indexing/jobs`)
      url.searchParams.set('api_key', apiKey)
      url.searchParams.set('limit', '20')
      url.searchParams.set('offset', '0')
      url.searchParams.set('type', activeTab)

      const res = await fetch(url.toString())
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to load indexing jobs')
      }
      const data = (await res.json()) as IndexJob[]
      setJobs(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load indexing jobs')
      setJobs([])
    } finally {
      setJobsLoading(false)
    }
  }

  const loadDomains = async () => {
    try {
      setDomainsLoading(true)
      const apiKey = await getApiKey()
      const url = new URL(`${apiBaseUrl}/indexing/indexnow/keys`)
      url.searchParams.set('api_key', apiKey)
      const res = await fetch(url.toString())
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to load domains')
      }
      const data = await res.json()
      setDomains(data.keys || [])
    } catch (err: any) {
      setError(err.message || 'Failed to load domains')
      setDomains([])
    } finally {
      setDomainsLoading(false)
    }
  }

  const handleRegisterDomain = async () => {
    if (!newDomain.trim()) {
      setError('Enter a domain to register.')
      return
    }
    try {
      setRegistering(true)
      setError(null)
      const apiKey = await getApiKey()
      const res = await fetch(`${apiBaseUrl}/indexing/indexnow/keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: apiKey, domain: newDomain.trim() }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Failed to register domain')

      setPendingSetup({
        id: '',
        host: data.host,
        key: data.key,
        key_location: data.key_location,
        verified: false,
      })
      setNewDomain('')
      await loadDomains()
    } catch (err: any) {
      setError(err.message || 'Failed to register domain')
    } finally {
      setRegistering(false)
    }
  }

  const handleVerifyDomain = async (host: string) => {
    try {
      setVerifyingHost(host)
      setError(null)
      const apiKey = await getApiKey()
      const res = await fetch(`${apiBaseUrl}/indexing/indexnow/keys/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: apiKey, domain: host }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        // The API returns the specific reason (404, wrong contents, etc.)
        throw new Error(data.error || 'Verification failed')
      }
      if (pendingSetup?.host === host) setPendingSetup(null)
      await loadDomains()
    } catch (err: any) {
      setError(err.message || 'Verification failed')
    } finally {
      setVerifyingHost(null)
    }
  }

  const handleDeleteDomain = async (keyId: string) => {
    try {
      setError(null)
      const apiKey = await getApiKey()
      const url = new URL(`${apiBaseUrl}/indexing/indexnow/keys/${keyId}`)
      url.searchParams.set('api_key', apiKey)
      const res = await fetch(url.toString(), { method: 'DELETE' })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to remove domain')
      }
      await loadDomains()
    } catch (err: any) {
      setError(err.message || 'Failed to remove domain')
    }
  }

  const handleCreateJob = async () => {
    const lines = urlsInput
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
    const uniqueUrls = Array.from(new Set(lines))
    if (!uniqueUrls.length) {
      setError('Please paste at least one URL.')
      return
    }
    if (uniqueUrls.length > 10000) {
      setError('You can check or submit up to 10,000 URLs at a time.')
      return
    }

    try {
      setIsSubmitting(true)
      setError(null)
      const apiKey = await getApiKey()
      if (!apiKey) {
        setError('No API key found. Please create one on the API Key page.')
        return
      }

      const res = await fetch(`${apiBaseUrl}/indexing/jobs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: apiKey,
          type: activeTab,
          urls: uniqueUrls,
          source: 'web',
        }),
      })

      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        // The API explains rejections in `details` (e.g. which host isn't
        // verified) — showing only `error` throws that away.
        throw new Error([data.error, data.details].filter(Boolean).join(' — ') || 'Failed to create job')
      }

      setUrlsInput('')
      await loadJobs()
      if (data.job_id) {
        // Auto-select the new job
        const newJob: IndexJob = {
          id: data.job_id,
          type: data.type,
          total_urls: data.total_urls,
          processed_urls: 0,
          status: data.status,
          created_at: new Date().toISOString(),
        }
        setSelectedJob(newJob)
        loadJobUrls(data.job_id, apiKey)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create job')
    } finally {
      setIsSubmitting(false)
    }
  }

  const loadJobUrls = async (jobId: string, apiKey?: string) => {
    try {
      setJobUrlsLoading(true)
      setError(null)
      const key = apiKey || (await getApiKey())
      if (!key) {
        setError('No API key found. Please create one on the API Key page.')
        return
      }
      const url = new URL(`${apiBaseUrl}/indexing/jobs/${jobId}/urls`)
      url.searchParams.set('api_key', key)
      url.searchParams.set('limit', '500')
      url.searchParams.set('offset', '0')

      const res = await fetch(url.toString())
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to load job URLs')
      }
      const data = (await res.json()) as IndexJobUrl[]
      setJobUrls(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load job URLs')
      setJobUrls([])
    } finally {
      setJobUrlsLoading(false)
    }
  }

  const handleSelectJob = async (job: IndexJob) => {
    setSelectedJob(job)
    setJobUrls([])
    // Also refresh this specific job's latest data from API
    try {
      const key = await getApiKey()
      const jobRes = await fetch(
        `${apiBaseUrl}/indexing/jobs/${job.id}?api_key=${encodeURIComponent(key)}`
      )
      if (jobRes.ok) {
        const freshJob = await jobRes.json()
        setSelectedJob(freshJob)
      }
    } catch {
      // non-critical; we already set the stale job
    }
    await loadJobUrls(job.id)
  }

  const handleExportCsv = async (job: IndexJob) => {
    try {
      setError(null)
      const apiKey = await getApiKey()
      if (!apiKey) {
        setError('No API key found. Please create one on the API Key page.')
        return
      }
      const url = new URL(`${apiBaseUrl}/indexing/jobs/${job.id}/export`)
      url.searchParams.set('api_key', apiKey)

      const res = await fetch(url.toString())
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to export CSV')
      }
      const blob = await res.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = `gistmag-indexing-job-${job.id}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(downloadUrl)
    } catch (err: any) {
      setError(err.message || 'Failed to export CSV')
    }
  }

  const renderJobSummary = () => {
    if (!selectedJob) {
      return (
        <div className="text-sm text-gray-600 dark:text-gray-300">
          Select a job from the left to see its progress and results.
        </div>
      )
    }

    const { total_urls, processed_urls, indexed_count, submitted_count, status, type } = selectedJob
    const progress = total_urls ? Math.round((processed_urls / total_urls) * 100) : 0
    const showIndeterminate = status === 'running' && progress === 0
    const countLabel =
      type === 'check'
        ? `${indexed_count ?? 0} indexed`
        : `${submitted_count ?? processed_urls} submitted`

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Job summary
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              {processed_urls} / {total_urls} URLs processed · {countLabel}
            </p>
          </div>
          <div>
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                status === 'complete'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  : status === 'running'
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                  : status === 'failed'
                  ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                  : 'bg-gray-100 text-gray-800 dark:bg-slate-800 dark:text-gray-200'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 rounded-full bg-gray-200 dark:bg-slate-800 overflow-hidden">
            <div
              className={`h-2 rounded-full bg-primary-500 transition-all ${
                showIndeterminate ? 'animate-pulse' : ''
              }`}
              style={{
                width: showIndeterminate
                  ? '30%'
                  : `${Math.min(100, Math.max(0, progress))}%`,
              }}
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium">
              {type === 'check' ? 'Index Check' : 'Index Submit'}:
            </span>{' '}
            {countLabel}
          </div>
          <button
            type="button"
            onClick={() => handleExportCsv(selectedJob)}
            className="inline-flex items-center rounded-md border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-800"
          >
            Export CSV
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Indexing Tools
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Check whether your pages are indexed in Google using your GistMag credits.
        </p>
      </div>

      <div className="mb-6 border-b border-gray-200 dark:border-slate-800">
        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setActiveTab('check')}
            className={`whitespace-nowrap border-b-2 px-1 pb-2 text-sm font-medium ${
              activeTab === 'check'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-300 dark:hover:text-gray-100 dark:hover:border-slate-600'
            }`}
          >
            Index Check
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('submit')}
            className={`whitespace-nowrap border-b-2 px-1 pb-2 text-sm font-medium ${
              activeTab === 'submit'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-300 dark:hover:text-gray-100 dark:hover:border-slate-600'
            }`}
          >
            Index Submit
          </button>
        </nav>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {activeTab === 'submit' && (
        <div className="mb-6 space-y-4">
          <div className="rounded-xl border border-blue-200 dark:border-blue-900/40 bg-blue-50/70 dark:bg-blue-900/20 p-4">
            <h2 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
              Index Submit notifies {INDEXNOW_ENGINES}
            </h2>
            <p className="text-xs text-blue-800 dark:text-blue-300">
              Submissions use the IndexNow protocol, which tells participating search engines
              the moment you publish or update a page.{' '}
              <strong>Google does not participate in IndexNow</strong> — use{' '}
              <strong>Index Check</strong> to monitor your Google coverage.
            </p>
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Your domains
              </h2>
              <button
                type="button"
                onClick={loadDomains}
                className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
              >
                Refresh
              </button>
            </div>
            <p className="mb-3 text-xs text-gray-600 dark:text-gray-300">
              IndexNow verifies ownership with a key file on your site. Register each domain
              once, upload its file, then verify — after that you can submit URLs any time.
            </p>

            <div className="flex gap-2">
              <input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRegisterDomain()
                }}
                placeholder="example.com"
                className="flex-1 rounded-md border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                type="button"
                onClick={handleRegisterDomain}
                disabled={registering || !newDomain.trim()}
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {registering ? 'Adding...' : 'Add domain'}
              </button>
            </div>

            {pendingSetup && (
              <div className="mt-4 rounded-md border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 p-3">
                <p className="text-xs font-semibold text-amber-900 dark:text-amber-200 mb-2">
                  Almost there — upload this file to {pendingSetup.host}
                </p>
                <dl className="space-y-1.5 text-xs text-amber-900 dark:text-amber-200">
                  <div>
                    <dt className="font-medium">File name</dt>
                    <dd className="font-mono break-all">{pendingSetup.key}.txt</dd>
                  </div>
                  <div>
                    <dt className="font-medium">File contents</dt>
                    <dd className="font-mono break-all">{pendingSetup.key}</dd>
                  </div>
                  <div>
                    <dt className="font-medium">Must be reachable at</dt>
                    <dd className="font-mono break-all">{pendingSetup.key_location}</dd>
                  </div>
                </dl>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const blob = new Blob([pendingSetup.key], { type: 'text/plain' })
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `${pendingSetup.key}.txt`
                      a.click()
                      URL.revokeObjectURL(url)
                    }}
                    className="rounded-md border border-amber-300 dark:border-amber-800 px-3 py-1.5 text-xs font-medium text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                  >
                    Download file
                  </button>
                  <button
                    type="button"
                    onClick={() => handleVerifyDomain(pendingSetup.host)}
                    disabled={verifyingHost === pendingSetup.host}
                    className="rounded-md border border-transparent bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-60"
                  >
                    {verifyingHost === pendingSetup.host ? 'Verifying...' : "I've uploaded it — verify"}
                  </button>
                </div>
              </div>
            )}

            {domainsLoading ? (
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">Loading domains...</p>
            ) : domains.length === 0 ? (
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                No domains registered yet. Add one above to start submitting URLs.
              </p>
            ) : (
              <ul className="mt-3 divide-y divide-gray-200 dark:divide-slate-800">
                {domains.map((d) => (
                  <li key={d.id} className="flex items-start justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                        {d.host}
                      </p>
                      {d.verified ? (
                        <p className="mt-0.5 text-xs text-green-700 dark:text-green-400">
                          Verified — ready to submit
                        </p>
                      ) : (
                        <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-400 break-words">
                          {d.last_error || 'Not verified yet — upload the key file, then verify.'}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 gap-2">
                      {!d.verified && (
                        <button
                          type="button"
                          onClick={() => handleVerifyDomain(d.host)}
                          disabled={verifyingHost === d.host}
                          className="text-xs text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-60"
                        >
                          {verifyingHost === d.host ? 'Verifying...' : 'Verify'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeleteDomain(d.id)}
                        className="text-xs text-red-600 dark:text-red-400 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: job creation + recent jobs */}
        <div className="space-y-6 lg:col-span-1">
          <div className="rounded-xl border border-gray-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {activeTab === 'check' ? 'Run an Index Check' : 'Submit URLs'}
            </h2>
            <p className="mb-3 text-xs text-gray-600 dark:text-gray-300">
              Paste up to 10,000 URLs, one per line. Each URL uses 1 credit.
              {activeTab === 'submit' && ' URLs must be on a verified domain.'}
            </p>
            <textarea
              className="w-full h-40 rounded-md border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="https://www.example.co.uk/&#10;https://www.example.co.uk/about&#10;..."
              value={urlsInput}
              onChange={(e) => setUrlsInput(e.target.value)}
            />
            <div className="mt-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>
                {urlStats.total} lines · {urlStats.unique} unique URLs
              </span>
              <span>1 credit per unique URL</span>
            </div>
            <button
              type="button"
              onClick={handleCreateJob}
              disabled={isSubmitting || urlStats.unique === 0 || (activeTab === 'submit' && !hasVerifiedDomain)}
              className="mt-3 inline-flex w-full items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {activeTab === 'check'
                ? isSubmitting
                  ? 'Starting index check...'
                  : 'Start index check'
                : isSubmitting
                  ? 'Submitting...'
                  : 'Submit URLs'}
            </button>
            {activeTab === 'submit' && !hasVerifiedDomain && (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                Verify at least one domain above before submitting.
              </p>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Recent jobs
              </h2>
              <button
                type="button"
                onClick={loadJobs}
                className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
              >
                Refresh
              </button>
            </div>
            {jobsLoading ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">Loading jobs...</p>
            ) : jobs.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                No {activeTab === 'check' ? 'index checks' : 'submissions'} yet.
              </p>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-slate-800">
                {jobs.map((job) => {
                  const progress = job.total_urls
                    ? Math.round((job.processed_urls / job.total_urls) * 100)
                    : 0
                  const label =
                    job.type === 'check'
                      ? `${job.indexed_count ?? 0} indexed`
                      : `${job.submitted_count ?? job.processed_urls} submitted`
                  return (
                    <li key={job.id}>
                      <button
                        type="button"
                        onClick={() => handleSelectJob(job)}
                        className="w-full text-left py-2"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                              {new Date(job.created_at).toLocaleString()}
                            </p>
                            <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">
                              {job.total_urls} URLs · {label}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {progress}%
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {job.status}
                            </p>
                          </div>
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Right: selected job summary + URL table */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-xl border border-gray-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 shadow-sm p-4">
            {renderJobSummary()}
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                URLs in job
              </h2>
              {selectedJob && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Showing first {jobUrls.length} URLs
                </span>
              )}
            </div>
            {selectedJob && (
              <p className="mb-3 text-[11px] text-gray-500 dark:text-gray-400">
                <span className="font-medium">Rate limited</span> means Google temporarily blocked the
                check (for example, due to too many requests). It does not necessarily mean the URL is
                not indexed – you can retry those URLs in a new job.
              </p>
            )}
            {!selectedJob ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Select a job to see individual URL results.
              </p>
            ) : jobUrlsLoading ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">Loading URLs...</p>
            ) : jobUrls.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                No URL rows found yet. If this is a new job, please wait a moment and refresh.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-800 text-xs">
                  <thead className="bg-gray-50 dark:bg-slate-900/60">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">
                        URL
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">
                        Status
                      </th>
                      {selectedJob.type === 'check' && (
                        <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">
                          Indexed?
                        </th>
                      )}
                      {selectedJob.type === 'submit' && (
                        <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">
                          Google status
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-slate-800">
                    {jobUrls.map((row) => (
                      <tr key={row.id}>
                        <td className="px-3 py-2 max-w-xs truncate text-gray-900 dark:text-gray-100">
                          <a
                            href={row.url}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:underline"
                          >
                            {row.url}
                          </a>
                        </td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                          {row.status === 'failed' && row.raw_result?.reason === 'rate_limited' ? (
                            <span className="inline-flex items-center rounded-full bg-orange-100 dark:bg-orange-900/30 px-2 py-0.5 text-[11px] font-medium text-orange-800 dark:text-orange-300">
                              Rate limited
                            </span>
                          ) : (
                            row.status
                          )}
                        </td>
                        {selectedJob.type === 'check' && (
                          <td className="px-3 py-2">
                            {row.status === 'failed' && row.raw_result?.reason === 'rate_limited' ? (
                              <span className="inline-flex items-center rounded-full bg-orange-100 dark:bg-orange-900/30 px-2 py-0.5 text-[11px] font-medium text-orange-800 dark:text-orange-300">
                                Rate limited
                              </span>
                            ) : row.is_indexed == null ? (
                              <span className="text-gray-500 dark:text-gray-400">—</span>
                            ) : row.is_indexed ? (
                              <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-2 py-0.5 text-[11px] font-medium text-green-800 dark:text-green-300">
                                Indexed
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-yellow-100 dark:bg-yellow-900/30 px-2 py-0.5 text-[11px] font-medium text-yellow-800 dark:text-yellow-300">
                                Not indexed
                              </span>
                            )}
                          </td>
                        )}
                        {selectedJob.type === 'submit' && (
                          <td className="px-3 py-2">
                            {row.status === 'done' ? (
                              <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-2 py-0.5 text-[11px] font-medium text-green-800 dark:text-green-300">
                                Submitted
                              </span>
                            ) : row.status === 'failed' ? (
                              <span className="inline-flex items-center rounded-full bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-[11px] font-medium text-red-800 dark:text-red-300">
                                {row.submit_status || 'Failed'}
                              </span>
                            ) : row.status === 'pending' ? (
                              <span className="text-gray-500 dark:text-gray-400">Pending</span>
                            ) : (
                              <span className="text-gray-700 dark:text-gray-200">
                                {row.submit_status || '—'}
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

